import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './ProductsPages.css';
import ProductsPsHeader from '../../components/ProductsPsHeader';
import TitleAndContentCard from '../../components/TitleAndContentCard';
import ExpandableCard from '../../components/ExpandableCard';
import CheckCircle from '../../icons/CheckCircle';
import WhyChooseOurPms from '../../components/WhyChooseOurPms';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function PointOfSale() {
    const langPrefix = useLanguagePrefix();
    const { t } = useTranslation();

    const features = [
        { title: t('Built for Every Property Type'), content: t('Whether you run a boutique hotel, luxury resort, hostel, or vacation rental, Prostay adapts to your operation—without compromise.') },
        { title: t('Everything Connected, Nothing Complicated'), content: t('All modules—from PMS and POS to Channel Manager and Messaging—work together in one seamless platform.') },
        { title: t('Fully Functional, Even Offline'), content: t('With native apps for Windows, Mac, iPads, and Android tablets, you stay operational—even without internet.') },
        { title: t('Scales With You'), content: t('Whether you’re managing 10 rooms or 200+, Prostay grows with your business—no need to switch platforms later.') },
    ];
    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleActiveIndex = (index) => {
        setActiveIndex(index);
    }

    return (
        <div>
            <Helmet>
                <title>Hotel Point of Sale Systems, Restaurant POS Systems - Prostay</title>
                <meta name="description" content="Run restaurants, bars, and in-room dining with Tableview POS. Fully integrated with Prostay PMS for real-time posting and F&B reporting." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/point-of-sale/"} />

                <link rel="alternate" href="https://www.prostay.com/en/point-of-sale/" />
                <link rel="alternate" href="https://www.prostay.com/es/point-of-sale/" />

                <meta property="og:title" content="Hotel Point of Sale Systems, Restaurant POS Systems - Prostay" />
                <meta property="og:description" content="Run restaurants, bars, and in-room dining with Tableview POS. Fully integrated with Prostay PMS for real-time posting and F&B reporting." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/point-of-sale/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Hotel Point of Sale Systems, Restaurant POS Systems - Prostay" />
                <meta name="twitter:description" content="Run restaurants, bars, and in-room dining with Tableview POS. Fully integrated with Prostay PMS for real-time posting and F&B reporting." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />

            </Helmet>
            <div className='product-page'>
                <ProductsPsHeader />
                <div className='d-flex justify-content-center product-page-content'>
                    <div className='page-content'>
                        <div className='d-flex justify-content-between py-2 py-md-5' style={{ flexWrap: 'wrap' }}>
                            <div className='col-12 col-md-5'>
                                <h2>{t('A POS That Speaks Hospitality')}</h2>
                            </div>
                            <div className='col-12 col-md-5'>
                                <p className='body-large'>
                                    {t('Tableview is designed to handle the complexities of hotel and resort operations. With full integration into Prostay’s PMS, your F&B outlets can directly post charges to guest rooms, track spending by outlet, and streamline billing at checkout. Use it at the bar, in the restaurant, on mobile devices for tableside orders, or on tablets for remote service.')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='d-flex features-carousel-wrapper'>
                    <div className='d-flex gap-3 features-carousel pb-5'>
                        {
                            features.map((feature, index) =>
                                <TitleAndContentCard title={feature.title} content={feature.content} width={384} height={178} onClick={() => handleActiveIndex(index)} />
                            )
                        }
                    </div>
                </div>
                <div className='d-flex justify-content-center py-2 py-md-5' style={{ flexWrap: 'wrap' }}>
                    <div className='page-content d-flex justify-content-between gap-4'>
                        <div className='col-12 col-md-4'>
                            <div className={selectedTabIndex == 0 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(0)}>
                                <label className='label-medium'> {t('Table Management')} </label>
                                {
                                    selectedTabIndex == 0 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 1 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(1)}>
                                <label className='label-medium'> {t('Room Charges')} </label>
                                {
                                    selectedTabIndex == 1 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 2 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(2)}>
                                <label className='label-medium'>{t('Menu Management')} </label>
                                {
                                    selectedTabIndex == 2 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 3 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(3)}>
                                <label className='label-medium'>{t('Offline Mode')} </label>
                                {
                                    selectedTabIndex == 3 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 4 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(4)}>
                                <label className='label-medium'> {t('Integrated Payments')} </label>
                                {
                                    selectedTabIndex == 4 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 5 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(5)}>
                                <label className='label-medium'> {t('Reporting & Analytics')} </label>
                                {
                                    selectedTabIndex == 5 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                        </div>
                        <div className='col tab-content'>
                            {
                                selectedTabIndex == 0 &&
                                <>
                                    <h4>{t('Table Management')}</h4>
                                    <img src='/images/postables.svg' alt={"Room planning"} />
                                    <p>
                                        {t('Tableview lets you visually manage tables, sections, and servers in real time. Customize layouts for each outlet, assign servers, and track open tabs to deliver efficient service and stay in control even during peak hours.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Visual table layout per outlet')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Real-time tab management')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Manage dine-in, takeout, and delivery')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Monitor turnover and seating efficiency')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 1 &&
                                <>
                                    <h4>{t('Post Charges Directly to Guest Rooms')}</h4>
                                    {/*<img src='/images/room-planning.png' alt={"Room planning"} />*/}
                                    <p>
                                        {t('Tableview seamlessly connects with the Prostay PMS to allow staff to post restaurant or bar charges directly to a guest’s room.')}
                                    </p>
                                    <p>
                                        {t('Whether it’s a spa drink or a dinner tab, charges are instantly linked to the reservation for easy billing and a smooth checkout experience.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Post to room with one click')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Live sync with the PMS')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Supports multiple outlets and guests')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Minimizes errors and billing confusion')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Auto-syncs to folio and guest profile')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 2 &&
                                <>
                                    <h4>{t('Menu Management')}</h4>
                                    {/*<img src='/images/room-planning.png' alt={"Room planning"} />*/}
                                    <p>
                                        {t('Create and manage menus for each outlet with categories, modifiers, pricing, and availability settings. Run specials, change items on the fly, and manage stock directly from your POS—no technical knowledge needed.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Dynamic menu builder')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Per-outlet pricing and availability')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Item modifiers (e.g., cooking preferences)')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Real-time menu sync across devices')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 3 &&
                                <>
                                    <h4>{t('Offline Mode')}</h4>
                                    {/* <img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('Never let downtime disrupt your operations. Tableview’s offline mode ensures that your team can continue taking orders and posting transactions even without an internet connection. Data is synced automatically once you’re back online.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Fully functional without internet')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Prevents lost sales and delays')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Auto-sync when reconnected')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Ideal for remote or outdoor venues')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 4 &&
                                <>
                                    <h4>{t('Integrated Payments')}</h4>
                                    {/*<img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('Accept payments quickly and securely with integrated card readers and payment gateways. Whether you’re taking cash, credit cards, room charges, or mixed payments, Tableview keeps transactions simple and transparent.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Integrated with leading payment providers')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Multiple payment methods supported')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Fast checkout and receipt printing')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Payment reconciliation within Prostay')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Reduced staff training time')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 5 &&
                                <>
                                    <h4>{t('Reporting & Analytics')}</h4>
                                    {/*  <img src='/images/room-planning.png' alt={"Room planning"} /> */}
                                    <p>
                                        {t('Stay on top of your F&B business with real-time reports on sales, staff activity, product performance, and outlet revenue.')}
                                    </p>
                                    <p>
                                        {t('All data is accessible in Tableview and can be synced with Prostay’s accounting module.')}
                                    </p>
                                    <h5 className='my-4'>{t('Room Planning')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Sales by item, outlet, and category')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Staff performance tracking')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Top-selling items and times of day')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('End-of-day reports and reconciliation')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Exportable to accounting/finance tools')}</p>
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
                <div className='d-flex justify-content-center mt-5 d-none d-md-flex'>
                    <div className='page-content d-flex w-100 justify-content-center align-items-center' style={{ textAlign: "center" }}>
                        <div className='col-lg-6 d-flex flex-column align-items-center'>
                            <h3 style={{ textAlign: "center", maxWidth: '582px' }}>{t('Revolutionize Your Operations with Our PMS Platform and Integrated Hotel Point of Sale Systems')}</h3>
                            <p className='body-large' style={{ textAlign: "center", maxWidth: '582px' }}>
                                {t('Cutting-edge Property Management System designed to revolutionize the way hotels, and resorts from boutique to large-scale, operate and serve their guests.')}
                            </p>
                        </div>
                    </div>
                </div>
                <div className='d-flex justify-content-center bottom-padding'>
                    <div className='page-content '>
                        <div className='white-card p-3 p-md-5 d-flex justify-content-between mt-3 mt-lg-5 gap-4 gap-lg-1' style={{ flexWrap: "wrap" }}>
                            <div className='col-12 col-lg-5'>
                                <h4>{t('Streamline reservations management and front office operations')}</h4>
                                <p className='mt-4'>
                                    {t('The promotions module allows businesses to set dynamic pricing strategies, offer seasonal discounts, and track the performance of promotional campaigns in real time.')}
                                </p>
                                <p>
                                    {t('Integrated A/B testing tools help refine marketing efforts for maximum engagement.')}
                                </p>
                                <a href={`${langPrefix}/request-demo/`} style={{ textDecoration: 'none' }}><button className='light-blue-filled-btn-small mt-5'>
                                    {t('Request Demo')}
                                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13 5.99992H1Z" fill="white" />
                                        <path d="M8.33333 1.33325L13 5.99992M13 5.99992L8.33333 10.6666M13 5.99992H1" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button></a>
                            </div>
                            <div className='col-12 col-lg-6'>
                                <img src='/images/posdetails.png' style={{ maxWidth: "100%" }} alt={"Room planning"} />
                            </div>
                        </div>
                    </div>
                </div>
                <WhyChooseOurPms
                    headerTitle="Take Charge with Hotel PMS Today!"
                    headerDescription="Cutting-edge Property Management System designed to revolutionize the way hotels, and resorts from boutique to large-scale, operate and serve their guests."
                    sectionTitle={t("What Sets Our Hotel Point-of-Sale System & Restaurant POS Apart?")}
                    sectionDescription={t("Find answers to the most common questions about Tableview, our POS system for hotels, resorts, and restaurants.")}
                    cards={[
                        { title: t("Can I post charges to guest rooms from the POS?"), description: t("Absolutely. Tableview is directly connected to the Prostay PMS, so your team can post F&B charges straight to a guest’s folio in real time.") },
                        { title: t("Does Tableview support multiple outlets or restaurants?"), description: t("Yes, you can manage multiple restaurants, bars, and service areas all from one system—each with its own layout, staff, and menu.") },
                        { title: t("Does Tableview support offline mode?"), description: t("Yes, Tableview works even when internet connectivity is lost. Orders and transactions are synced once the system is back online.") },
                        { title: t("What hardware is Tableview compatible with?"), description: t("Tableview runs natively on Windows PCs, Android tablets, and iPads. It also supports major brands of receipt printers, cash drawers, barcode scanners, and integrated payment terminals. We can help you choose the right setup for your venue.") }
                    ]}
                />
            </div>
        </div>
    );
}

export default PointOfSale;
